import React, { useCallback, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  // TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Paper
} from "@material-ui/core";
import { HelpOutline } from "@material-ui/icons";
import useStyles from "@/styles/dialogStyle";
import { useTranslation } from "@/langs/useTranslation";
import styles from "./index.module.less";
import { IconUploadMedia } from "@/components/Icon";
import { s3upload } from "@/helpers/s3upload";
import { toast } from "@/apis/request";
import MediaPreview from "./MediaPreview";
import { formatSecondsToTime } from "@/helpers/utils";
// import UploadImage from "@/components/UploadImage";

// 获取图片尺寸的辅助函数
const getImageDimensions = file => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height, src: img.src });
      // URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
};

// 获取视频元数据（尺寸和时长）的辅助函数
const getVideoMetadata = file => {
  return new Promise(resolve => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({
        dimensions: { width: video.videoWidth, height: video.videoHeight },
        duration: Math.round(video.duration),
        src: video.src
      });
      // URL.revokeObjectURL(video.src); // 清理
    };
    video.src = URL.createObjectURL(file);
  });
};

const UploadCMSResourceDialog = ({
  open,
  onClose,
  setRefreshCapacity,
  reloadTable,
  onSuccess,
  cap
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [mediaInfo, setMediaInfo] = useState({
    file: null,
    name: "",
    type: "",
    size: "",
    width: 0,
    height: 0,
    duration: "",
    src: ""
  });
  const [analysing, setAnalysing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const uploadRef = useRef(null);

  const onDrop = useCallback(async acceptedFiles => {
    if (acceptedFiles.length === 0) {
      toast.error(t({ defaultMessage: "不支持的格式。" }));
      return;
    }
    setAnalysing(true);
    const file = acceptedFiles[0];
    try {
      const fileType = file.type.split("/")[0]; // 'image' or 'video'
      const [previewType, type] = file.type.split("/");
      const result = {
        name: file.name,
        type,
        previewType,
        size: file.size
      };

      if (fileType === "image") {
        // 获取图片尺寸
        const imageData = await getImageDimensions(file);
        result.dimensions = {
          width: imageData.width,
          height: imageData.height
        };
        result.src = imageData.src;
      } else if (fileType === "video") {
        // 获取视频尺寸和时长
        const videoData = await getVideoMetadata(file);
        result.dimensions = videoData.dimensions;
        result.duration = videoData.duration;
        result.src = videoData.src;
      }
      setMediaInfo({
        file,
        name: file.name,
        type: result.type,
        previewType: result.previewType,
        size: `${result.dimensions.width}*${result.dimensions.height}`,
        width: result.dimensions.width,
        height: result.dimensions.height,
        duration: result.duration,
        src: result.src
      });
    } catch (error) {
      toast.error(t({ defaultMessage: "解析失败" }));
      console.error(error);
    } finally {
      setAnalysing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noDrag: uploading,
    noClick: uploading,
    multiple: false,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "video/mp4": [".mp4", ".m4v"], // MPEG-4（包括 .mp4 和 .m4v）
      "video/quicktime": [".mov"] // QuickTime MOV
    }
  });

  const handleDeleteFile = useCallback(e => {
    e.stopPropagation();
    setMediaInfo({});
  }, []);

  const onCloseDialog = useCallback(
    forceClose => {
      if (!forceClose && uploading) return;
      setMediaInfo({});
      setUploading(false);
      setAnalysing(false);
      setPercentCompleted(0);
      onClose();
    },
    [uploading]
  );

  const onOk = async () => {
    // if (!canUpload) return;
    if (mediaInfo.previewType === "image") {
      console.log(Number(mediaInfo.file.size));
      if (Number(mediaInfo.file.size) > 15 * 1024 * 1024) {
        toast.error(t({ defaultMessage: "图片最大不能超过15MB" }));
        return;
      }
    }
    if (mediaInfo.previewType === "video") {
      if (Number(mediaInfo.file.size) > 10 * 1024 * 1024 * 1024) {
        toast.error(t({ defaultMessage: "视频最大不能超过10GB" }));
        return;
      }
    }
    if (cap > 0 && Number(mediaInfo.file.size) > cap) {
      toast.error(t({ defaultMessage: "文件大小超过可用空间" }));
      return;
    }
    setUploading(true);
    const params = {
      type: 1,
      width: mediaInfo.width,
      height: mediaInfo.height
    };
    if (mediaInfo.duration) params.duration = mediaInfo.duration;
    uploadRef.current = await s3upload(
      { files: [mediaInfo.file] },
      "",
      t,
      t({ defaultMessage: "上传素材成功" }),
      percentCompleted,
      setPercentCompleted,
      () => onCloseDialog(true),
      setRefreshCapacity,
      reloadTable,
      true,
      false,
      false,
      true,
      false,
      false,
      undefined,
      "",
      params,
      onSuccess
    );
  };

  return (
    <Dialog
      maxWidth={false}
      className={classes.dialog}
      open={open}
      onClose={onCloseDialog}
      aria-labelledby="add-file"
      aria-describedby="add-file-confirmation"
      disableBackdropClick={uploading || analysing}
      disableEscapeKeyDown={uploading || analysing}
    >
      <DialogTitle
        id="add-file"
        style={{ paddingTop: "12px !important", lineHeight: "20px" }}
      >
        {t({ defaultMessage: "上传素材" })}
        <Tooltip
          title={t({
            defaultMessage:
              "Nubis 目前支持 JPEG 和 PNG 图像格式。视频必须使用 H.264 编解码器进行编码。支持的文件格式包括 MPEG-4 和 MOV。编解码器是一种软件或硬件，用于压缩数据以加快传输速度，并将接收到的数据解压缩回其原始格式。H.264 编解码器通常用于视频内容的录制、压缩和分发。一个视频文件格式可能包含一个或多个编解码器。如果您的 MOV 或 MPEG-4 格式视频文件未通过审核，请检查用于编码视频的编解码器是否为 H.264。"
          })}
        >
          <HelpOutline />
        </Tooltip>
      </DialogTitle>
      <DialogContent className="uploadFileOnlyDialog" style={{ width: 560 }}>
        {!mediaInfo.file ? (
          <div
            className={`${styles.uploadArea} ${
              isDragActive ? styles.dragActive : ""
            } ${uploading ? styles.uploading : ""}`}
            {...getRootProps()}
          >
            {analysing ? (
              <Box display="flex" alignItems="center">
                <CircularProgress size={14} />
                <Typography style={{ marginLeft: 10, color: "#91919F" }}>
                  {t({ defaultMessage: "解析中" })}
                </Typography>
              </Box>
            ) : (
              <>
                <IconUploadMedia size={58} />
                <Typography className={styles.uploadTip}>
                  {t({ defaultMessage: "上传素材" })}
                </Typography>
                <Typography className={styles.uploadTip2}>
                  {t({ defaultMessage: "将文件拖到此处 或点击上传" })}
                </Typography>
              </>
            )}
          </div>
        ) : (
          <div>
            <Typography variant="subtitle1" style={{ marginTop: 16 }}>
              {t({ defaultMessage: "预览" })}
            </Typography>
            <MediaPreview type={mediaInfo.previewType} src={mediaInfo.src} gc />
            <Typography variant="subtitle1" style={{ marginBottom: 16 }}>
              {t({ defaultMessage: "上传素材" })}
            </Typography>
            <Paper
              style={{
                background: "#F8F9FC",
                padding: 16,
                boxShadow: undefined
              }}
            >
              <Typography style={{ marginBottom: 12 }} variant="body1">
                {t({ defaultMessage: "名称: {name}" }, mediaInfo)}
              </Typography>
              <Typography style={{ marginBottom: 12 }} variant="body1">
                {t({ defaultMessage: "类型: {type}" }, mediaInfo)}
              </Typography>
              <Typography
                style={{ marginBottom: mediaInfo.duration ? 12 : 0 }}
                variant="body1"
              >
                {t({ defaultMessage: "尺寸: {size}" }, mediaInfo)}
              </Typography>
              {mediaInfo.duration && (
                <Typography variant="body1">
                  {t(
                    { defaultMessage: "时长: {duration}" },
                    { duration: formatSecondsToTime(mediaInfo.duration) }
                  )}
                </Typography>
              )}
            </Paper>
          </div>
        )}

        <Box display="flex" justifyContent="center" sx={{ marginTop: 32 }}>
          <Button
            variant="contained"
            color="primary"
            style={{ minWidth: 150, height: 40 }}
            onClick={onOk}
            disabled={!mediaInfo.file || uploading}
          >
            {uploading && (
              <CircularProgress size={14} style={{ marginRight: 8 }} />
            )}
            {uploading
              ? t(
                  { defaultMessage: "正在上传 {percentCompleted}%" },
                  { percentCompleted }
                )
              : t({ defaultMessage: "确认" })}
          </Button>
          {uploading ? (
            <Button
              color="secondary"
              variant="outlined"
              type="button"
              style={{ width: 150, height: 40, marginLeft: 10 }}
              onClick={() => {
                if (uploadRef.current) {
                  uploadRef.current.abort();
                }
                onCloseDialog(true);
              }}
              // disabled={uploading}
            >
              {t({ defaultMessage: "取消上传" })}
            </Button>
          ) : (
            <Button
              variant="contained"
              // color="secondary"
              style={{
                width: 150,
                height: 40,
                background: "rgba(0, 175, 198, 0.1)",
                color: uploading ? undefined : "#00afc6",
                marginLeft: 10
              }}
              onClick={onCloseDialog}
            >
              {t({ defaultMessage: "取消" })}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadCMSResourceDialog;
